/**
 * @namespace Stores
 * @category Stores
 * @module datatable-store
 * */

import datatableApi from '@/api/datatableApi'
import * as R from 'ramda'

import Vue from 'vue'
import { getQueryStringValue } from '@/utils/querystring'
import i18n from '@/i18n'
const shouldLog =
  (getQueryStringValue('verbose') || '').includes('1') ||
  (getQueryStringValue('verbose') || '').includes('datatable')

const defaultItemsPerPage =
  import.meta.env.VITE_DATATABLE_ITEMS_PER_REQUEST || 30
const defaultMaxItemsToFetch =
  import.meta.env.VITE_DATATABLE_POOLING_MAX || 5000
const defaultPooling = true

export default {
  namespaced: true,
  state: {
    infos: {
      //table1:{pooling:true,maxItemsToFetch:100,showMaxItemsToFetchAlert:true} //Pooling example
      zones: {
        itemsPerPage: 30,
      },
    },
    data: {},
    checkboxProps: {
      modelValue: false,
      icon: '',
      tooltip: '',
      iconColor: 'var(--color-main)',
      iconStyle: { fontSize: '16px' },
      emitName: '',
      shouldDisplay: true,
    },

    //Iternally watched by Datatable.vue
    //Use-case: datatable async render mode (batching)
    disablePaginationVisually: false,
    currentPage: 0,
    pageLength: 0,
  },
  getters: {
    getItemsPerPage: (state, getters) => (tableName) => {
      return (
        getters.getTableInfos(tableName).itemsPerPage || defaultItemsPerPage
      )
    },
    getTableItems: (state) => (tableName) => {
      shouldLog && console.log('store.datatable.getTableItems', tableName)
      return state.data[tableName] || []
    },
    getTableInfos: (state) => (tableName) => {
      shouldLog && console.log('store.datatable.getTableInfos', tableName)
      return state.infos[tableName] || {}
    },
    getCheckboxProps: (state) => state.checkboxProps,
  },
  mutations: {
    setTableItems(state, datatableApiResult = {}) {
      shouldLog &&
        console.log(
          'mutation.setTableItems',
          R.omit(['items'], datatableApiResult)
        )

      if (!datatableApiResult.name || !datatableApiResult.items) {
        console.warn('Datatable-Store::setTableItems: Skip invalid dataset')
        return
      }

      if (state.data[datatableApiResult.name] === undefined) {
        Vue.set(state.data, datatableApiResult.name, [])
      }
      if (state.infos[datatableApiResult.name] === undefined) {
        Vue.set(state.infos, datatableApiResult.name, {})
      }

      state.data[datatableApiResult.name] = datatableApiResult.items || []
      state.infos[datatableApiResult.name] = {
        ...state.infos[datatableApiResult.name],
        ...(datatableApiResult.infos || {}),
      }
    },
    pushTableItems(state, { name, items }) {
      shouldLog && console.log('pushTableItems', name, items.length, 'items')
      state.data[name] = state.data[name].concat(items)
    },
    setItemsPerPage(state, { tableName, itemsPerPage }) {
      if (state.infos[tableName]) {
        state.infos[tableName].itemsPerPage = itemsPerPage
      }
    },
    setTablePooling(state, { tableName, pooling }) {
      if (state.infos[tableName]) {
        state.infos[tableName].pooling = pooling
      }
    },
    registerTable(state, options) {
      let infos = {
        ...(state.infos[options.name] || {}),
        itemsPerPage: defaultItemsPerPage,
        pooling: defaultPooling,
      }
      let keys = [
        'maxItemsToFetch',
        'itemsPerPage',
        'pooling',
        'showMaxItemsToFetchAlert',
      ]
      keys.forEach((key) => {
        infos[key] =
          options[key] !== undefined ? options[key] : infos[key] || undefined
      })
      Vue.set(state.infos, options.name, infos)
      Vue.set(state.data, options.name, [])
    },
    resetCheckboxProps(state) {
      state.checkboxProps = {
        modelValue: false,
        icon: '',
        tooltip: '',
        iconColor: 'var(--color-main)',
        iconStyle: { fontSize: '16px' },
        emitName: '',
      }
    },
  },
  actions: {
    setTableItems({ commit }, data) {
      commit('setTableItems', data)
    },
    setItemsPerPage({ commit }, { tableName, itemsPerPage }) {
      commit('setItemsPerPage', { tableName, itemsPerPage })
    },
    setTablePooling({ commit }, { tableName, pooling }) {
      commit('setTablePooling', { tableName, pooling })
    },
    registerTable({ commit }, options = {}) {
      commit('registerTable', options)
      datatableApi.registerTable(options)
    },
    async fetchTableItems({ commit, state, dispatch }, options = {}) {
      shouldLog &&
        console.log('action.fetchTableItems', {
          name: options.name,
        })
      let infos = state.infos[options.name] || {}

      let itemsPerPage =
        options.itemsPerPage || infos.itemsPerPage || defaultItemsPerPage
      let maxItemsToFetch = infos.maxItemsToFetch || defaultMaxItemsToFetch

      let payload = {
        infos,
        ...options,
        itemsPerPage,
      }

      commit('setTableItems', await datatableApi.fetchTableItems(payload))

      infos = state.infos[options.name] || {}

      const shouldPooling =
        infos.pooling !== undefined ? infos.pooling : defaultPooling

      if (
        infos.maxItemsToFetch !== -1 &&
        shouldPooling &&
        infos.lastPage * itemsPerPage > maxItemsToFetch
      ) {
        if (infos.showMaxItemsToFetchAlert !== false) {
          dispatch(
            'alert/addAlert',
            {
              type: 'warning',
              title: i18n.t('datatable.pooling_limit_title'),
              text: 'alerts.DATATABLE_MAX_ELEMENTS_LIMIT_HIT',
              params: {
                expected: maxItemsToFetch,
                output: infos.lastPage * itemsPerPage,
              },
            },
            {
              root: true,
            }
          )
        }
        infos.lastPage = maxItemsToFetch / itemsPerPage
      }

      //shouldLog && console.log("Should pooling?", {shouldPooling,infos,currentPage: infos.currentPage,lastPage: infos.lastPage,});

      if (shouldPooling && infos.currentPage < infos.lastPage) {
        for (var page = infos.currentPage + 1; page <= infos.lastPage; page++) {
          shouldLog &&
            console.log(
              'fetchTableItems::pooling',
              options.name,
              'pooling',
              `${page}/${infos.lastPage}`
            )
          commit(
            'pushTableItems',
            await datatableApi.fetchTableItems({
              infos,
              ...options,
              page,
              itemsPerPage,
            })
          )
        }
      }
    },
  },
}
